﻿import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/shared/components/ui/dialog";
import { Separator } from "@/shared/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/shared/components/ui/tooltip";
import { TenantVariableForm } from "./tenant-variable-form";
import { TenantVariableValueField } from "./tenant-variable-value-field";
import { ChevronDown, ChevronRight, Pencil, Trash2 } from "lucide-react";
import { cn } from "@/shared/lib/utils";
import type {
  TenantVariableDraft,
  TenantVariableValueDraft,
  TenantVariableView,
} from "@/domains/tenant";
import type { Json } from "@/types/supabase.types";

type TenantVariableListProps = {
  isDeletingDefinition?: boolean;
  isSavingDefinition?: boolean;
  isSavingValue?: boolean;
  variables: TenantVariableView[];
  onCreateVariable: (draft: TenantVariableDraft) => void;
  onUpdateVariable: (variableId: string, draft: TenantVariableDraft) => void;
  onDeleteVariable: (variableId: string) => void;
  onSaveValue: (value: TenantVariableValueDraft) => void;
};

type VariableAnimationState = "entering" | "exiting";

const VARIABLE_ROW_ANIMATION_MS = 220;

function groupVariables(variables: TenantVariableView[]) {
  const grouped = new Map<string, TenantVariableView[]>();
  for (const variable of variables) {
    const group = variable.variable_group || "general";
    grouped.set(group, [...(grouped.get(group) ?? []), variable]);
  }
  return [...grouped.entries()];
}

function variableLabel(variable: TenantVariableView) {
  return variable.variable_name_frontend ?? variable.variable_name;
}

function sortVariables(variables: TenantVariableView[]) {
  return [...variables].sort((a, b) => {
    const groupCompare = (a.variable_group ?? "").localeCompare(
      b.variable_group ?? "",
    );
    if (groupCompare !== 0) return groupCompare;
    if (a.variable_order !== b.variable_order) {
      return a.variable_order - b.variable_order;
    }
    return variableLabel(a).localeCompare(variableLabel(b));
  });
}

function areJsonValuesEqual(first: Json | null, second: Json | null) {
  if (Object.is(first, second)) return true;
  return JSON.stringify(first) === JSON.stringify(second);
}

function applyPendingValues(
  variables: TenantVariableView[],
  pendingValues: Map<string, Json | null>,
) {
  return variables.map((variable) =>
    pendingValues.has(variable.variable_id)
      ? {
          ...variable,
          current_value: pendingValues.get(variable.variable_id) ?? null,
        }
      : variable,
  );
}

function variableIds(variables: TenantVariableView[]) {
  return new Set(variables.map((variable) => variable.variable_id));
}

function clearTimeouts(timeouts: ReturnType<typeof setTimeout>[]) {
  for (const timeout of timeouts) {
    clearTimeout(timeout);
  }
  timeouts.splice(0, timeouts.length);
}

export function TenantVariableList({
  isDeletingDefinition = false,
  isSavingDefinition = false,
  isSavingValue = false,
  variables,
  onCreateVariable,
  onUpdateVariable,
  onDeleteVariable,
  onSaveValue,
}: TenantVariableListProps) {
  const [isCreating, setIsCreating] = useState(false);
  const [editingVariableId, setEditingVariableId] = useState<string | null>(
    null,
  );
  const [deletingVariableId, setDeletingVariableId] = useState<string | null>(
    null,
  );
  const [expandedVariableIds, setExpandedVariableIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [renderedVariables, setRenderedVariables] = useState(() => variables);
  const [animatedVariableIds, setAnimatedVariableIds] = useState<
    Map<string, VariableAnimationState>
  >(() => new Map());
  const [pendingValues, setPendingValues] = useState<Map<string, Json | null>>(
    () => new Map(),
  );
  const didMountRef = useRef(false);
  const previousVariablesRef = useRef(variables);
  const pendingValuesCleanupRef = useRef(variables);
  const animationTimeoutsRef = useRef<ReturnType<typeof setTimeout>[]>([]);
  const variablesWithPendingValues = applyPendingValues(
    renderedVariables,
    pendingValues,
  );

  useEffect(() => {
    if (didMountRef.current) {
      const previousVariables = previousVariablesRef.current;
      previousVariablesRef.current = variables;
      const nextIds = variableIds(variables);
      const previousIds = variableIds(previousVariables);

      setRenderedVariables((current) => {
        const removedVariables = current.filter(
          (variable) => !nextIds.has(variable.variable_id),
        );
        return [...variables, ...removedVariables];
      });

      setAnimatedVariableIds((current) => {
        const next = new Map(current);

        for (const variable of variables) {
          if (!previousIds.has(variable.variable_id)) {
            next.set(variable.variable_id, "entering");
          }
        }

        for (const variable of previousVariables) {
          if (!nextIds.has(variable.variable_id)) {
            next.set(variable.variable_id, "exiting");
          }
        }

        return next;
      });

      clearTimeouts(animationTimeoutsRef.current);
      const timeout = setTimeout(() => {
        setRenderedVariables(variables);
        setAnimatedVariableIds((current) => {
          const next = new Map(current);
          for (const [variableId, state] of next) {
            if (state === "entering" || !nextIds.has(variableId)) {
              next.delete(variableId);
            }
          }
          return next;
        });
      }, VARIABLE_ROW_ANIMATION_MS);
      animationTimeoutsRef.current.push(timeout);
    } else {
      didMountRef.current = true;
      previousVariablesRef.current = variables;
      setRenderedVariables(variables);
    }
  }, [variables]);

  useEffect(() => {
    const animationTimeouts = animationTimeoutsRef.current;
    return () => {
      clearTimeouts(animationTimeouts);
    };
  }, []);

  useEffect(() => {
    if (pendingValuesCleanupRef.current !== variables) {
      pendingValuesCleanupRef.current = variables;
      setPendingValues((current) => {
        let changed = false;
        const next = new Map(current);

        for (const variable of variables) {
          if (!next.has(variable.variable_id)) continue;
          const pendingValue = next.get(variable.variable_id) ?? null;
          if (areJsonValuesEqual(variable.current_value, pendingValue)) {
            next.delete(variable.variable_id);
            changed = true;
          }
        }

        return changed ? next : current;
      });
    }
  }, [variables]);

  const variablesByParentId = useMemo(() => {
    const byParentId = new Map<string, TenantVariableView[]>();
    for (const variable of variablesWithPendingValues) {
      if (!variable.parent_variable_id) continue;
      byParentId.set(variable.parent_variable_id, [
        ...(byParentId.get(variable.parent_variable_id) ?? []),
        variable,
      ]);
    }
    return byParentId;
  }, [variablesWithPendingValues]);
  const globalVariables = useMemo(
    () =>
      sortVariables(
        variablesWithPendingValues.filter(
          (variable) =>
            !variable.is_definition_editable && !variable.parent_variable_id,
        ),
      ),
    [variablesWithPendingValues],
  );
  const tenantVariables = useMemo(
    () =>
      sortVariables(
        variablesWithPendingValues.filter(
          (variable) =>
            variable.is_definition_editable && !variable.parent_variable_id,
        ),
      ),
    [variablesWithPendingValues],
  );
  const editingVariable = variablesWithPendingValues.find(
    (variable) => variable.variable_id === editingVariableId,
  );
  const deletingVariable = variablesWithPendingValues.find(
    (variable) => variable.variable_id === deletingVariableId,
  );

  const handleCreate = (draft: TenantVariableDraft) => {
    onCreateVariable(draft);
    setIsCreating(false);
  };

  const handleUpdate = (draft: TenantVariableDraft) => {
    if (!editingVariableId) return;
    onUpdateVariable(editingVariableId, draft);
    setEditingVariableId(null);
  };

  const handleDelete = () => {
    if (!deletingVariableId) return;
    onDeleteVariable(deletingVariableId);
    setDeletingVariableId(null);
  };

  const handleSaveValue = (value: TenantVariableValueDraft) => {
    const variableId = value.variable_id;
    if (!variableId) return;

    setPendingValues((current) => {
      const next = new Map(current);
      next.set(variableId, value.value);
      return next;
    });
    onSaveValue(value);
  };

  const toggleVariable = (variableId: string) => {
    setExpandedVariableIds((current) => {
      const next = new Set(current);
      if (next.has(variableId)) {
        next.delete(variableId);
      } else {
        next.add(variableId);
      }
      return next;
    });
  };

  const renderVariable = (variable: TenantVariableView, depth = 0) => {
    const children = sortVariables(
      variablesByParentId.get(variable.variable_id) ?? [],
    );
    const hasChildren = children.length > 0;
    const isExpanded = expandedVariableIds.has(variable.variable_id);
    const label = variableLabel(variable);
    const fieldId = `tenant-variable-value-${variable.variable_id}`;
    const animationState = animatedVariableIds.get(variable.variable_id);

    return (
      <div
        key={variable.variable_id}
        className={cn(
          "grid gap-0",
          animationState === "entering" &&
            "motion-safe:animate-in motion-safe:fade-in-0 motion-safe:slide-in-from-top-1 motion-safe:duration-200",
          animationState === "exiting" &&
            "pointer-events-none opacity-60 motion-safe:animate-out motion-safe:fade-out-0 motion-safe:slide-out-to-right-2 motion-safe:duration-200",
        )}
        data-animation-state={animationState}
      >
        <div
          className="grid gap-3 p-3"
          data-variable-row
          style={{ paddingLeft: `${1 + depth * 1.25}rem` }}
        >
          <div className="grid gap-3 lg:grid-cols-[minmax(10rem,15rem)_minmax(14rem,1fr)_auto] lg:items-center">
            <div className="flex min-w-0 items-center gap-2">
              {hasChildren ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-sm"
                  className="-ml-2"
                  onClick={() => toggleVariable(variable.variable_id)}
                  aria-label={
                    isExpanded ? `Collapse ${label}` : `Expand ${label}`
                  }
                >
                  {isExpanded ? (
                    <ChevronDown className="h-4 w-4" />
                  ) : (
                    <ChevronRight className="h-4 w-4" />
                  )}
                </Button>
              ) : (
                <span className="w-6 shrink-0" />
              )}
              <label
                htmlFor={fieldId}
                className="min-w-0 text-sm leading-snug font-medium break-words"
              >
                {label}
              </label>
            </div>
            <div className="min-w-0 flex-1">
              {variable.variable_is_visible && (
                <TenantVariableValueField
                  id={fieldId}
                  label={label}
                  showLabel={false}
                  variable={variable}
                  isSaving={isSavingValue}
                  onSave={handleSaveValue}
                />
              )}
            </div>
            <div className="ml-auto flex shrink-0 gap-1 lg:justify-end">
              {variable.is_definition_editable && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      aria-label={`Edit ${label}`}
                      onClick={() => setEditingVariableId(variable.variable_id)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Edit</TooltipContent>
                </Tooltip>
              )}
              {variable.is_definition_deletable && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon-sm"
                      className="text-destructive hover:text-destructive"
                      aria-label={`Delete ${label}`}
                      disabled={isDeletingDefinition}
                      onClick={() =>
                        setDeletingVariableId(variable.variable_id)
                      }
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>Delete</TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </div>
        {hasChildren && isExpanded && (
          <div className="divide-y border-t bg-muted/20">
            {children.map((child) => renderVariable(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  const renderVariableTree = (
    title: string,
    description: string,
    sectionVariables: TenantVariableView[],
    options?: { showAdd?: boolean },
  ) => {
    const groupedVariables = groupVariables(sectionVariables);

    return (
      <section className="space-y-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1">
            <h3 className="text-base font-semibold">{title}</h3>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
          {options?.showAdd && (
            <Button type="button" onClick={() => setIsCreating(true)}>
              Add variable
            </Button>
          )}
        </div>

        {groupedVariables.length === 0 ? (
          <div className="rounded-md border border-dashed p-6 text-center text-sm text-muted-foreground">
            No variables configured.
          </div>
        ) : (
          groupedVariables.map(([group, groupVariables]) => (
            <section key={`${title}-${group}`} className="space-y-3">
              <h4 className="text-sm font-semibold capitalize">{group}</h4>
              <div className="divide-y rounded-md border">
                {groupVariables.map((variable) => renderVariable(variable))}
              </div>
            </section>
          ))
        )}
      </section>
    );
  };

  return (
    <div className="space-y-5">
      <Separator decorative={false} />
      {renderVariableTree(
        "Global Variables",
        "Reference defaults shared across tenants.",
        globalVariables,
      )}

      <Separator decorative={false} />
      {renderVariableTree(
        "Tenant Variables",
        "Configure organization defaults for analyses.",
        tenantVariables,
        { showAdd: true },
      )}

      <Dialog open={isCreating} onOpenChange={setIsCreating}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Add variable</DialogTitle>
            <DialogDescription>
              Create a tenant-specific variable and optional default value.
            </DialogDescription>
          </DialogHeader>
          <TenantVariableForm
            isSubmitting={isSavingDefinition}
            onCancel={() => setIsCreating(false)}
            onSubmit={handleCreate}
          />
        </DialogContent>
      </Dialog>

      <Dialog
        open={editingVariable !== undefined}
        onOpenChange={(open) => {
          if (!open) setEditingVariableId(null);
        }}
      >
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
          <DialogHeader>
            <DialogTitle>Edit variable</DialogTitle>
            <DialogDescription>
              Update the label, group, type, and default for this tenant
              variable.
            </DialogDescription>
          </DialogHeader>
          {editingVariable && (
            <TenantVariableForm
              initialValue={editingVariable}
              isSubmitting={isSavingDefinition}
              onCancel={() => setEditingVariableId(null)}
              onSubmit={handleUpdate}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog
        open={deletingVariable !== undefined}
        onOpenChange={(open) => {
          if (!open) setDeletingVariableId(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete variable</DialogTitle>
            <DialogDescription>
              This permanently deletes "
              {deletingVariable?.variable_name_frontend ??
                deletingVariable?.variable_name}
              " and its stored value.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setDeletingVariableId(null)}
            >
              Cancel
            </Button>
            <Button
              type="button"
              variant="destructive"
              disabled={isDeletingDefinition}
              onClick={handleDelete}
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
